import { inject, Injectable } from '@angular/core';
import { ContactIndividualEntity, EventsService, ProjectsEventsService, ProjectsSaleService } from '@app/client';
import { EventStateEnum } from '@app/core/enums/event.enum';
import { MessageService } from 'primeng/api';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { EventOutcomeForm } from './event-outcome.interface';

@Injectable({
  providedIn: 'root',
})
export class EventOutcomeFacade {
  private eventsService = inject(EventsService);
  private projectsSaleService = inject(ProjectsSaleService);
  private projectsEventsService = inject(ProjectsEventsService);
  private messageService = inject(MessageService);

  getEventOutcome(eventId: string): Observable<EventOutcomeForm> {
    return this.eventsService.findOne(eventId).pipe(
      switchMap((event) =>
        forkJoin({
          event: of(event),
          lastEventNote: this.eventsService.getLastEventNote(eventId),
          projectSale: event.projectSale ? this.projectsSaleService.findOne(event.projectSale) : of(null),
        }),
      ),
      map(({ event, lastEventNote, projectSale }) => {
        return {
          projectId: event.projectSale || event.projectPurchase || event.project,
          state: event.state,
          note: event.note,
          type: event.type,
          dispute: {
            reason: event.dispute?.reason,
            note: event.dispute?.note,
          },
          propertyAddress: event.propertyAddress?.formatted_address,
          contactAddress: (event.contactObj as ContactIndividualEntity).address?.formatted_address,
          openHouseParentEvent: event.openHouseParentEvent,
          oldNote: lastEventNote?.note,
          opportunityState: projectSale?.status.opportunity,
          rescheduleCount: event.rescheduleCount,
          userId: (event.user as { id: string })?.id || (event.user as unknown as string) || event.insertUser,
        };
      }),
    );
  }

  getAvailabilitySlot(userId: string, start: Date, end: Date, slot = 0.5) {
    return this.eventsService.getAvailabilitySlot({ userId, start: start.toISOString(), end: end.toISOString(), slot });
  }

  disputeEvent(eventId: string, disputeReason: number, disputeNote: string) {
    return this.projectsEventsService
      .disputeEvent({
        eventId,
        disputeReason,
        disputeNote,
      })
      .pipe(
        tap((res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Evento contestato con successo',
          });
        }),
      );
  }

  rescheduleEvent(eventId: string, event: { newDate: Date; note: string; bypassReminder: boolean }) {
    return this.projectsEventsService
      .rescheduleEvent({
        originEventId: eventId,
        newDate: event.newDate.toISOString(),
        note: event.note,
        bypassReminder: event.bypassReminder,
      })
      .pipe(
        tap((res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Evento riprogrammato con successo',
          });
        }),
      );
  }

  removeParticipantVisitEvent(eventId: string, note: string) {
    return this.eventsService
      .removeParticipantVisitEvent({
        eventId,
        note,
      })
      .pipe(
        tap((res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Partecipanti rimossi con successo',
          });
        }),
      );
  }

  updateAllParticipantsOpenHouse(eventId: string, note: string, state: EventStateEnum) {
    return this.eventsService
      .updateAllParticipantsOpenHouse({
        eventId,
        note,
        state,
      })
      .pipe(
        tap((res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Partecipanti aggiornati con successo',
          });
        }),
      );
  }

  updateEventStatus(eventId: string, status: EventStateEnum, note: string) {
    return this.projectsEventsService
      .updateEventStatus(eventId, {
        status,
        note,
      })
      .pipe(
        tap((res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Stato evento aggiornato con successo',
          });
        }),
      );
  }
}
